import { type Vec3, vec3 } from './Vec3';

/**
 * Solid primitives as wireframe meshes.
 *
 * ## Hard and smooth edges
 *
 * Every edge carries a flag saying whether it is a real crease in the surface or an
 * artefact of tessellating a curved one. It decides how the edge is drawn:
 *
 * - A **hard** edge — the rim of a cylinder, an edge of a cube — is always drawn, solid
 *   where it faces the viewer and dashed where it does not.
 * - A **smooth** edge — one of the thirty-two vertical lines a tessellated cylinder is
 *   built from — is drawn only where it forms the silhouette.
 *
 * Without that distinction a cylinder renders as a barrel of thirty-two vertical lines.
 * With it, the same mesh renders as a textbook draws it: two elliptical rims and two
 * straight sides. The alternative — inferring smoothness from the dihedral angle — needs a
 * threshold, and a threshold is a number that is wrong for some solid. The builder knows
 * which edges it invented, so it says so.
 *
 * ## Winding
 *
 * Face vertex rings run counter-clockwise seen from outside the solid. That is what makes
 * the sign of the projected normal a correct front/back test.
 *
 * **The rings below run `(cos θ, y, −sin θ)`, and the minus sign is load-bearing.** In a
 * right-handed system with y up, z points towards the viewer, so looking *down* the y axis
 * puts +z at the bottom of the page: a plain `(cos θ, sin θ)` ring therefore runs clockwise
 * from above, and every face built on it comes out wound inward.
 *
 * That was a real defect, not a hypothetical one. Six of the eight primitives shipped with
 * every face inverted, which swapped the hidden and visible halves of every prism, cylinder,
 * cone, sphere and frustum — the drawings were inside out. It survived because the only
 * winding test covered the cube, whose vertices are written out by hand, and because the
 * silhouette tests are invariant under a global normal flip. `mesh.test.ts` now checks the
 * outward normal of every face of every primitive.
 */

export const SolidKind = {
  Cube: 'cube',
  Cuboid: 'cuboid',
  Prism: 'prism',
  Pyramid: 'pyramid',
  Cylinder: 'cylinder',
  Cone: 'cone',
  Sphere: 'sphere',
  Frustum: 'frustum',
} as const;
export type SolidKind = (typeof SolidKind)[keyof typeof SolidKind];

export interface Edge {
  readonly a: number;
  readonly b: number;
  /** False for edges that only exist because a curved surface was tessellated. */
  readonly hard: boolean;
  /** Indices of the faces meeting at this edge. One entry means a boundary edge. */
  readonly faces: readonly number[];
}

/**
 * A circle the solid was built from, described as a circle rather than as the ring of chords
 * that approximates it.
 *
 * The rim of a cylinder, a cone or a frustum *is* a circle, and a parallel projection carries a
 * circle to an ellipse exactly. Saying so here is what lets the drawing be an ellipse instead
 * of forty-eight chords — smooth at any magnification, which no number of chords ever is.
 *
 * The point at parameter `t` is `centre + u·cos t + v·sin t`, and `vertices[i]` is the mesh
 * vertex standing at `t = 2πi/n`. Both are needed: the circle to draw with, and the vertices to
 * ask the face classifier which parts of it the body hides.
 */
export interface Rim {
  readonly centre: Vec3;
  readonly u: Vec3;
  readonly v: Vec3;
  readonly vertices: readonly number[];
}

export interface Mesh {
  readonly vertices: readonly Vec3[];
  /** Vertex rings, counter-clockwise seen from outside. */
  readonly faces: readonly (readonly number[])[];
  readonly edges: readonly Edge[];
  /** The circles among its edges, when it has any. */
  readonly rims?: readonly Rim[];
}

export interface SolidParameters {
  readonly kind: SolidKind;
  /** Extent along x. */
  readonly width: number;
  /** Extent along y — the upright axis. */
  readonly height: number;
  /** Extent along z. */
  readonly depth: number;
  /** Base polygon sides, for prisms and pyramids. Ignored by the others. */
  readonly sides: number;
  /** Top radius as a fraction of the base, for a frustum. Ignored by the others. */
  readonly taper: number;
}

export const DEFAULT_SOLID: SolidParameters = Object.freeze({
  kind: SolidKind.Cube,
  width: 100,
  height: 100,
  depth: 100,
  sides: 6,
  taper: 0.5,
});

/** Below this length, an edge is a coincidence of indices rather than a line. */
const DEGENERATE_EDGE = 1e-9;

/** How many segments a curved surface is tessellated into. */
const CURVE_SEGMENTS = 48;

/** How many segments the sphere's silhouette and its rings use. */
const SPHERE_SEGMENTS = 64;

export function buildMesh(parameters: SolidParameters): Mesh {
  const { kind, width, height, depth, sides, taper } = parameters;
  const rx = width / 2;
  const ry = height / 2;
  const rz = depth / 2;
  const n = Math.max(3, Math.round(sides));

  switch (kind) {
    case SolidKind.Cube:
      // A cube is a cuboid the tool keeps square; the mesh is the same shape.
      return box(rx, rx, rx);

    case SolidKind.Cuboid:
      return box(rx, ry, rz);

    case SolidKind.Prism:
      return barrel(n, rx, ry, rz, 1, true);

    case SolidKind.Cylinder:
      return barrel(CURVE_SEGMENTS, rx, ry, rz, 1, false);

    case SolidKind.Pyramid:
      return cone(n, rx, ry, rz, true);

    case SolidKind.Cone:
      return cone(CURVE_SEGMENTS, rx, ry, rz, false);

    case SolidKind.Frustum:
      return barrel(CURVE_SEGMENTS, rx, ry, rz, Math.max(0.01, Math.min(1, taper)), false);

    case SolidKind.Sphere:
      return sphere(rx, ry, rz);
  }
}

/* ------------------------------------------------------------------------- box */

function box(rx: number, ry: number, rz: number): Mesh {
  const vertices = [
    vec3(-rx, -ry, rz),
    vec3(rx, -ry, rz),
    vec3(rx, ry, rz),
    vec3(-rx, ry, rz),
    vec3(-rx, -ry, -rz),
    vec3(rx, -ry, -rz),
    vec3(rx, ry, -rz),
    vec3(-rx, ry, -rz),
  ];

  const faces = [
    [0, 1, 2, 3], // front  (+z)
    [5, 4, 7, 6], // back   (-z)
    [1, 5, 6, 2], // right  (+x)
    [4, 0, 3, 7], // left   (-x)
    [3, 2, 6, 7], // top    (+y)
    [4, 5, 1, 0], // bottom (-y)
  ];

  return withEdges(vertices, faces, () => true);
}

/* --------------------------------------------------------- prisms and cylinders */

/**
 * A solid with two parallel n-gon caps.
 *
 * `taper` scales the top cap, which turns a prism into a frustum. The side edges are hard
 * for a prism, where they are genuine creases, and smooth for a cylinder or frustum, where
 * they only exist because a circle was cut into segments.
 */
function barrel(
  n: number,
  rx: number,
  ry: number,
  rz: number,
  taper: number,
  hardSides: boolean
): Mesh {
  const vertices: Vec3[] = [];

  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2;
    vertices.push(vec3(Math.cos(angle) * rx, -ry, -Math.sin(angle) * rz));
  }
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2;
    vertices.push(vec3(Math.cos(angle) * rx * taper, ry, -Math.sin(angle) * rz * taper));
  }

  const faces: number[][] = [];

  // Sides, wound counter-clockwise seen from outside.
  for (let i = 0; i < n; i++) {
    const next = (i + 1) % n;
    faces.push([i, next, n + next, n + i]);
  }

  // Bottom seen from below is the reverse of bottom seen from above.
  faces.push(Array.from({ length: n }, (_, i) => n - 1 - i));
  faces.push(Array.from({ length: n }, (_, i) => n + i));

  // A side edge joins a bottom vertex to the top vertex above it; the cap rims do not.
  const mesh = withEdges(vertices, faces, (a, b) => hardSides || Math.abs(a - b) !== n);

  /*
   * Only a *round* barrel has rims. `hardSides` is what separates a cylinder from a prism, and
   * a prism's base is a hexagon: declaring it a circle would draw an ellipse through its
   * corners and quietly round off the shape the figure is about.
   */
  if (hardSides) return mesh;

  const ring = (offset: number, y: number, scale: number): Rim => ({
    centre: vec3(0, y, 0),
    u: vec3(rx * scale, 0, 0),
    v: vec3(0, 0, -rz * scale),
    vertices: Array.from({ length: n }, (_, i) => offset + i),
  });

  return { ...mesh, rims: [ring(0, -ry, 1), ring(n, ry, taper)] };
}

/* ----------------------------------------------------------- pyramids and cones */

function cone(n: number, rx: number, ry: number, rz: number, hardSides: boolean): Mesh {
  const vertices: Vec3[] = [];

  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2;
    vertices.push(vec3(Math.cos(angle) * rx, -ry, -Math.sin(angle) * rz));
  }
  const apex = vertices.length;
  vertices.push(vec3(0, ry, 0));

  const faces: number[][] = [];
  for (let i = 0; i < n; i++) {
    faces.push([i, (i + 1) % n, apex]);
  }
  faces.push(Array.from({ length: n }, (_, i) => n - 1 - i));

  // Edges to the apex are the tessellation artefacts on a cone; the base rim is not.
  const mesh = withEdges(vertices, faces, (a, b) => hardSides || (a !== apex && b !== apex));

  // A pyramid's base is a polygon, not a circle — see the note in `barrel`.
  if (hardSides) return mesh;

  return {
    ...mesh,
    rims: [
      {
        centre: vec3(0, -ry, 0),
        u: vec3(rx, 0, 0),
        v: vec3(0, 0, -rz),
        vertices: Array.from({ length: n }, (_, i) => i),
      },
    ],
  };
}

/* ---------------------------------------------------------------------- sphere */

/**
 * A sphere, drawn the way a textbook draws one.
 *
 * A tessellated sphere with every edge drawn is a ball of wire that reads as noise. What
 * makes a drawing legible is the silhouette plus a couple of reference rings, so the
 * latitude and longitude bands are built as smooth edges — leaving only the silhouette —
 * and the equator and a meridian are promoted to hard rings, whose far halves come out
 * dashed. That falls out of the same visibility rule as every other solid rather than
 * needing a special case downstream.
 *
 * **The meridian is a whole great circle, not half of one.** Two reasons, and the second is
 * the important one:
 *
 * - Half a meridian is a bare arc from pole to pole, which reads as a stray line rather
 *   than as a ring on a sphere.
 * - A sphere's silhouette and equator are both *invariant* under rotation about the upright
 *   axis. With only those two, yawing the sphere would change nothing on screen at all —
 *   the user would drag and see a dead object. The meridian is what makes the rotation
 *   visible, which for an interactive solid is not decoration.
 */
function sphere(rx: number, ry: number, rz: number): Mesh {
  const bands = 12;
  const segments = SPHERE_SEGMENTS / 2;
  const vertices: Vec3[] = [];
  const faces: number[][] = [];

  const index = (band: number, segment: number): number => band * segments + (segment % segments);

  for (let band = 0; band <= bands; band++) {
    const phi = (band / bands) * Math.PI - Math.PI / 2;
    for (let segment = 0; segment < segments; segment++) {
      const theta = (segment / segments) * Math.PI * 2;
      vertices.push(
        vec3(
          Math.cos(phi) * Math.cos(theta) * rx,
          Math.sin(phi) * ry,
          -Math.cos(phi) * Math.sin(theta) * rz
        )
      );
    }
  }

  for (let band = 0; band < bands; band++) {
    for (let segment = 0; segment < segments; segment++) {
      faces.push([
        index(band, segment),
        index(band, segment + 1),
        index(band + 1, segment + 1),
        index(band + 1, segment),
      ]);
    }
  }

  const equator = Math.round(bands / 2);
  const mesh = withEdges(vertices, faces, () => false);

  // Being hard is what makes an edge's far half draw dashed, so the two reference rings are
  // promoted here rather than being drawn by a special case downstream.
  const opposite = segments / 2;
  const hardened = mesh.edges.map((edge) => {
    const bandOf = (vertex: number) => Math.floor(vertex / segments);
    const segmentOf = (vertex: number) => vertex % segments;

    const sameBand = bandOf(edge.a) === bandOf(edge.b);
    const onEquator = sameBand && bandOf(edge.a) === equator;

    // Both halves of the great circle through the poles: the +x column and the −x one.
    const column = segmentOf(edge.a);
    const onMeridian =
      !sameBand && column === segmentOf(edge.b) && (column === 0 || column === opposite);

    return onEquator || onMeridian ? { ...edge, hard: true } : edge;
  });

  return { vertices: mesh.vertices, faces: mesh.faces, edges: hardened };
}

/* -------------------------------------------------------------------- plumbing */

/**
 * Derives the edge list from the faces, recording which faces meet at each edge.
 *
 * The adjacency is what the visibility rule runs on: an edge is hidden when every face
 * meeting it faces away. Building it here rather than in each primitive means a new solid
 * only has to describe its faces correctly.
 */
function withEdges(
  vertices: readonly Vec3[],
  faces: readonly (readonly number[])[],
  isHard: (a: number, b: number) => boolean
): Mesh {
  const byKey = new Map<string, { a: number; b: number; faces: number[] }>();

  faces.forEach((ring, faceIndex) => {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if (a === undefined || b === undefined || a === b) continue;

      const key = a < b ? `${String(a)}:${String(b)}` : `${String(b)}:${String(a)}`;
      const existing = byKey.get(key);
      if (existing === undefined) {
        byKey.set(key, { a, b, faces: [faceIndex] });
      } else if (!existing.faces.includes(faceIndex)) {
        existing.faces.push(faceIndex);
      }
    }
  });

  const edges: Edge[] = [];
  for (const { a, b, faces: adjacent } of byKey.values()) {
    /*
     * Degenerate edges are dropped here rather than downstream. A UV sphere's pole rings
     * collapse to a single point, so sixty-four of its "edges" join two indices that name
     * the same position. They are not geometry: they cannot be seen, they cannot be
     * clicked, and letting them through means every consumer — painter, hit test, marquee
     * bounds — has to know to skip them.
     */
    const from = vertices[a];
    const to = vertices[b];
    if (from === undefined || to === undefined) continue;
    if (Math.hypot(from.x - to.x, from.y - to.y, from.z - to.z) <= DEGENERATE_EDGE) continue;

    edges.push({ a, b, hard: isHard(a, b), faces: adjacent });
  }

  return { vertices, faces, edges };
}
